import React, { useMemo } from 'react';
import { getOfficeConfig } from '../config/offices';

const TYPE_LABELS = {
  conference: 'Conference Rooms',
  meeting: 'Meeting Rooms',
  huddle: 'Huddle Rooms',
  office: 'Offices',
  cubicle: 'Cubicles',
};

const TYPE_ICONS = {
  conference: '🏢',
  meeting: '📋',
  huddle: '💬',
  office: '🖥️',
  cubicle: '💻',
};

const TYPE_ORDER = ['conference', 'meeting', 'huddle', 'office', 'cubicle'];

/**
 * RoomList - Mobile list view showing rooms grouped by type
 * with availability status and quick-book buttons.
 */
export default function RoomList({ availability = {}, onRoomSelect }) {
  const config = getOfficeConfig();
  const rooms = config.rooms.filter(r => r.bookable !== false);

  const roomsByType = useMemo(() => {
    const groups = {};
    rooms.forEach((room) => {
      if (!groups[room.type]) groups[room.type] = [];
      groups[room.type].push(room);
    });
    return groups;
  }, [rooms]);

  const now = new Date();
  const currentTimeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  return (
    <div className="room-list">
      {/* Quick-book buttons for cubicles and offices */}
      <div className="room-list-quick">
        {rooms.some((r) => r.type === 'cubicle') && (
          <QuickBookButton
            type="cubicle"
            rooms={rooms}
            availability={availability}
            onSelect={() => onRoomSelect?.('any-cubicle')}
          />
        )}
        {rooms.some((r) => r.type === 'office') && (
          <QuickBookButton
            type="office"
            rooms={rooms}
            availability={availability}
            onSelect={() => onRoomSelect?.('any-office')}
          />
        )}
      </div>

      {TYPE_ORDER.filter((type) => roomsByType[type]?.length > 0).map((type) => (
        <div key={type} className="room-list-group">
          <h3 className="room-list-group-heading">
            <span className="room-list-group-icon">{TYPE_ICONS[type]}</span>
            {TYPE_LABELS[type]}
            <span className="room-list-group-count">
              {roomsByType[type].filter((r) => !availability[r.id]?.currentlyBooked).length} available
            </span>
          </h3>

          <div className="room-list-items">
            {roomsByType[type].map((room) => {
              const roomAvail = availability[room.id];
              const isBooked = roomAvail?.currentlyBooked;
              const slots = roomAvail?.slots || [];
              const currentSlot = slots.find(
                (s) => s.start <= currentTimeStr && s.end > currentTimeStr
              );
              const nextSlot = slots.find((s) => s.start > currentTimeStr);

              return (
                <div
                  key={room.id}
                  className={`room-list-item ${isBooked ? 'room-list-item--booked' : 'room-list-item--available'}`}
                  onClick={() => onRoomSelect?.(room.id)}
                >
                  <div className="room-list-item-left">
                    <div className="room-list-item-name">{room.name}</div>
                    {isBooked && currentSlot ? (
                      <div className="room-list-item-detail">
                        {currentSlot.organizer || currentSlot.bookedBy || 'Reserved'} until {formatTime(currentSlot.end)}
                      </div>
                    ) : nextSlot ? (
                      <div className="room-list-item-detail">
                        Next: {formatTime(nextSlot.start)} - {nextSlot.organizer || 'Reserved'}
                      </div>
                    ) : (
                      <div className="room-list-item-detail">Open all day</div>
                    )}
                  </div>
                  <div className="room-list-item-right">
                    <span className={`room-list-badge ${isBooked ? 'badge-booked' : 'badge-available'}`}>
                      {isBooked ? 'In Use' : 'Open'}
                    </span>
                    {!isBooked && (
                      <button
                        className="room-list-book-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRoomSelect?.(room.id);
                        }}
                      >
                        Book
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function QuickBookButton({ type, rooms, availability, onSelect }) {
  const available = rooms.filter(
    (r) => r.type === type && !availability[r.id]?.currentlyBooked
  ).length;
  const label = type === 'cubicle' ? 'Any Cubicle' : 'Any Office';

  return (
    <button
      className={`room-list-quick-btn ${type}-quick-btn`}
      onClick={onSelect}
      disabled={available === 0}
    >
      <span className="quick-btn-label">{label}</span>
      <span className="quick-btn-count">{available} open</span>
    </button>
  );
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  let h, m;
  if (timeStr.includes('T')) {
    const d = new Date(timeStr.endsWith('Z') ? timeStr : timeStr + 'Z');
    const central = new Date(d.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    h = central.getHours();
    m = central.getMinutes();
  } else {
    [h, m] = timeStr.split(':').map(Number);
  }
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${m.toString().padStart(2, '0')} ${suffix}`;
}
